import React from 'react';
import { UnscheduledEvent } from '../types';
import { ERTS_REMOVED_CONFIDENCE } from '../constants';
import MovedFromDot from './MovedFromDot';
import { useColumnSort, SortAccessor } from '../utils/useColumnSort';
import { isLockedByOther } from '../services/lockService';

/**
 * Props for the ScheduledTable component.
 */
interface ScheduledTableProps {
  events: UnscheduledEvent[];
  onView: (event: UnscheduledEvent) => void;
  onEdit: (event: UnscheduledEvent) => void;
  /** Map of eventId → alias currently holding that event's edit lock. */
  lockOwners?: Record<string, string>;
  /** The current user's alias, so their own lock never disables their Edit button. */
  currentUserAlias?: string;
}

/** Sort accessors for the scheduled table columns. */
const SCHEDULED_SORT_ACCESSORS: Record<string, SortAccessor<UnscheduledEvent>> = {
  carrier: (e) => e.carrier || '',
  tailNumber: (e) => e.tailNumber || '',
  station: (e) => e.station || '',
  scheduledDate: (e) => e.timeDeclaredZulu || '',
  erts: (e) => e.returnToServiceTimeZulu || '',
  confidence: (e) =>
    e.returnToServiceConfidence && e.returnToServiceConfidence !== ERTS_REMOVED_CONFIDENCE
      ? e.returnToServiceConfidence
      : '',
  status: (e) => e.status || '',
};

/**
 * Table displaying scheduled maintenance records.
 * Columns: Carrier, Aircraft, Station, Scheduled Maintenance Event, Scheduled Date, ERTS, ERTS Confidence, Status.
 */
const ScheduledTable: React.FC<ScheduledTableProps> = ({
  events,
  onView,
  onEdit,
  lockOwners,
  currentUserAlias,
}) => {
  const { toggleSort, sortData, arrowFor } =
    useColumnSort<UnscheduledEvent>(SCHEDULED_SORT_ACCESSORS);
  const rows = sortData(events);
  return (
    <div className="events-section">
      <div className="events-count">
        {events.length} Scheduled Maintenance Record{events.length !== 1 ? 's' : ''}
      </div>
      <div className="table-wrapper">
        <table className="events-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => toggleSort('carrier')}>
                Carrier <span className="sort-arrow">{arrowFor('carrier')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('tailNumber')}>
                Aircraft <span className="sort-arrow">{arrowFor('tailNumber')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('station')}>
                Station <span className="sort-arrow">{arrowFor('station')}</span>
              </th>
              <th>Scheduled Maintenance Event</th>
              <th className="sortable" onClick={() => toggleSort('scheduledDate')}>
                Scheduled Date <span className="sort-arrow">{arrowFor('scheduledDate')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('erts')}>
                ERTS <span className="sort-arrow">{arrowFor('erts')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('confidence')}>
                ERTS Confidence <span className="sort-arrow">{arrowFor('confidence')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('status')}>
                Status <span className="sort-arrow">{arrowFor('status')}</span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr key={event.eventId}>
                <td>{event.carrier}</td>
                <td>
                  <span className="aircraft-link">{event.tailNumber}</span>
                </td>
                <td>{event.station}</td>
                <td className="discrepancy-cell">
                  {event.defects && event.defects.length > 0
                    ? event.defects.map((d) => (
                        <div key={d.id} className="defect-entry">
                          {d.id}) {d.text}
                        </div>
                      ))
                    : '—'}
                </td>
                <td>{event.timeDeclaredZulu || '—'}</td>
                <td>{event.returnToServiceTimeZulu || '—'}</td>
                <td>
                  {event.returnToServiceConfidence &&
                  event.returnToServiceConfidence !== ERTS_REMOVED_CONFIDENCE
                    ? event.returnToServiceConfidence
                    : '—'}
                </td>
                <td>
                  <span
                    className={`status-badge status-${event.status.toLowerCase().replace('_', '-')}`}
                  >
                    {event.status.replace('_', ' ')}
                  </span>
                  {event.movedFrom && <MovedFromDot fromRecordType={event.movedFrom} />}
                </td>
                <td className="actions-cell">
                  <button
                    className="action-btn view-btn"
                    type="button"
                    aria-label="View event"
                    onClick={() => onView(event)}
                  >
                    &#128065;
                  </button>
                  <button
                    className="action-btn edit-btn"
                    type="button"
                    aria-label="Edit event"
                    disabled={isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')}
                    title={
                      isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')
                        ? `Being edited by ${lockOwners?.[event.eventId]}`
                        : 'Edit event'
                    }
                    onClick={() => onEdit(event)}
                  >
                    &#9998;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduledTable;
